import { InstructionDateInput } from 'components/Form/InstructionDateInput'
import { Loading } from 'components/Loading'
import {
  TimeRangeInput,
  LocationSelectInput,
  DateInput,
} from 'components/UserInputs'
import { ContactDetailsSelect } from 'components/UserInputs/ContactDetailsSelect'
import { RadioInputGroup } from 'components/UserInputs/RadioInputGroup'
import { STUDENT_DC, USER_TYPE_OPTIONS } from '../../constants'
import { useFormikContext } from 'formik'
import moment from 'moment'
import { Flex, Box, Label } from 'theme-ui'
import {
  StudentCreateFormValues,
  ViewMode,
  ConfigUtils,
  TeacherData,
} from 'types'
import { formatLastDayToCompleteCourse } from 'utils/digitalCourseStudents'
import { msFromTimeString, formatTime } from 'utils/helpers'
import { ErrorMessage } from 'components/Form'

const maxMoment = moment().add(1, 'years')
const maxDate = {
  year: maxMoment.year(),
  month: maxMoment.month() + 1,
  day: maxMoment.date(),
}

export type InstructionDetailsFields =
  | 'userType'
  | 'status'
  | 'teachingOrg'
  | 'teachingOrgId'
  | 'countryCode'
  | 'instructionDate'
  | 'startTime'
  | 'endTime'
  | 'locationId'
  | 'contactOption'
export type InstructionDetailsFormValues = Pick<
  StudentCreateFormValues,
  InstructionDetailsFields
>

export const InstructionDetailsSection = ({
  isSupport,
  viewMode,
  isDisabledField,
  configUtils,
  teacher,
  shouldShowError = () => true,
  startTabIndex,
  isUserUsingNewOrgs,
  showDropDownPicker = false,
}: {
  isUserUsingNewOrgs: boolean
  isSupport: boolean
  viewMode: ViewMode
  configUtils: ConfigUtils
  teacher?: TeacherData
  isDisabledField: (fieldName: keyof InstructionDetailsFormValues) => boolean
  shouldShowError?: (fieldName: keyof InstructionDetailsFormValues) => boolean
  startTabIndex: number
  showDropDownPicker: boolean
}) => {
  const { values, errors, setFieldValue, handleBlur, validateForm } =
    useFormikContext<InstructionDetailsFormValues>()

  const isDCStudent = !!(
    values.userType === STUDENT_DC ||
    (values.countryCode && !configUtils.isCountryPublic(values.countryCode))
  )
  //is "YYYY-MM-DD" instructionDate before "YYYY-MM-DD" 14 days ago (from browser's TZ)
  const instructionMoreThan14DaysAgo = moment(values.instructionDate).isBefore(
    moment(moment().format('YYYY-MM-DD')).subtract({ days: 14 })
  )
  const isUserTypeDisabled =
    isDisabledField('userType') ||
    (isDCStudent
      ? values.status === 'completed'
      : showDropDownPicker
      ? false
      : instructionMoreThan14DaysAgo)

  return (
    <>
      <Flex variant={`forms.section.${viewMode}`}>
        {configUtils.isCountryPublic(values.countryCode) && (
          <>
            <Box>
              <RadioInputGroup
                options={USER_TYPE_OPTIONS}
                value={values.userType}
                name="userType"
                label="Study format"
                error={
                  shouldShowError('userType') ? errors.userType : undefined
                }
                setFieldValue={setFieldValue}
                onBlur={handleBlur}
                disabled={isUserTypeDisabled}
                tabIndex={startTabIndex}
              />
            </Box>
            <Box></Box>
          </>
        )}
        {isDisabledField('instructionDate') ? (
          (() => {
            const displayDate = moment(values.instructionDate).format(
              'MMM Do, YYYY'
            )
            const startTime = msFromTimeString(values.startTime)
            const endTime = msFromTimeString(values.endTime)
            const isTimeConfirmed =
              startTime !== undefined && endTime !== undefined
            const displayTime =
              startTime !== undefined && endTime !== undefined
                ? `from ${formatTime(startTime)} to ${formatTime(endTime)}`
                : ''
            return (
              <>
                <Box pb={40}>
                  <Label htmlFor="instructionDatePlaceholder">
                    {isTimeConfirmed
                      ? 'Instruction date and time'
                      : 'Instruction date'}
                  </Label>
                  <Box sx={{ width: 400 }}>
                    {values.instructionDate ? (
                      <>
                        {displayDate} {displayTime}
                      </>
                    ) : (
                      'not confirmed'
                    )}
                  </Box>
                  {isDCStudent && (
                    <Label
                      sx={{
                        paddingTop: '12px',
                        minHeight: 44,
                        opacity: 1,
                        fontWeight: 400,
                        fontSize: '14px',
                        pb: 40,
                      }}
                    >
                      {formatLastDayToCompleteCourse(values)}
                    </Label>
                  )}
                </Box>
                <Box></Box>
              </>
            )
          })()
        ) : (
          <>
            <Box>
              {showDropDownPicker ? (
                <DateInput
                  label="Instruction date"
                  name="instructionDate"
                  value={values.instructionDate}
                  disabled={isDisabledField('instructionDate')}
                  setFieldValue={(name, value: string) => {
                    setFieldValue(name, value.split('T').shift())
                  }}
                  error={
                    shouldShowError('instructionDate')
                      ? errors.instructionDate
                      : undefined
                  }
                  onBlur={handleBlur}
                  tabIndex={startTabIndex + 4}
                  maxDate={maxDate}
                />
              ) : (
                <>
                  <Label htmlFor="instructionDatePlaceholder">
                    Instruction date
                  </Label>
                  <InstructionDateInput
                    disabled={isDisabledField('instructionDate')}
                    currDate={
                      values.instructionDate
                        ? values.instructionDate
                        : undefined
                    }
                    onDateChange={(date: string) => {
                      setFieldValue('instructionDate', date)
                    }}
                    countryCode={values.countryCode}
                    teachingOrg={
                      isUserUsingNewOrgs
                        ? values.teachingOrgId
                        : values.teachingOrg
                    }
                    error={
                      shouldShowError('instructionDate')
                        ? !!errors.instructionDate
                        : undefined
                    }
                    userType={isDCStudent ? STUDENT_DC : values.userType}
                    isAdmin={isSupport}
                    tabIndex={startTabIndex + 1}
                  />
                </>
              )}

              {errors.instructionDate && (
                <ErrorMessage>
                  {shouldShowError('instructionDate') ? (
                    <span>{errors.instructionDate}</span>
                  ) : null}
                </ErrorMessage>
              )}
            </Box>
            <Box>
              {teacher ? (
                <TimeRangeInput
                  start={{
                    name: 'startTime',
                    label: 'Start time',
                    value: values.startTime,
                    error: shouldShowError('startTime')
                      ? errors.startTime
                      : undefined,
                  }}
                  end={{
                    name: 'endTime',
                    label: 'End time',
                    value: values.endTime,
                    error: shouldShowError('endTime')
                      ? errors.endTime
                      : undefined,
                  }}
                  setFieldValue={setFieldValue}
                  validateForm={validateForm}
                  defaultRange={teacher.avgInstructionLength}
                  onBlur={handleBlur}
                  disabled={
                    isDisabledField('startTime') || isDisabledField('endTime')
                  }
                  tabIndex={startTabIndex + 2}
                />
              ) : (
                <Loading />
              )}
            </Box>
          </>
        )}

        {teacher ? (
          <>
            <Box>
              <LocationSelectInput
                name="locationId"
                label="Teaching location"
                disabled={isDisabledField('locationId')}
                value={values.locationId}
                setFieldValue={setFieldValue}
                validateForm={validateForm}
                onBlur={handleBlur}
                ids={teacher.teachingLocations ?? []}
                error={
                  shouldShowError('locationId') ? errors.locationId : undefined
                }
                tabIndex={startTabIndex + 3}
              />
            </Box>
            <Box>
              <ContactDetailsSelect
                name="contactOption"
                label="Teacher contact details"
                contactOptions={teacher.contact}
                setFieldValue={setFieldValue}
                onBlur={handleBlur}
                disabled={isDisabledField('contactOption')}
                error={
                  shouldShowError('contactOption')
                    ? errors.contactOption
                    : undefined
                }
                value={values.contactOption}
                tabIndex={startTabIndex + 4}
              />
            </Box>
          </>
        ) : (
          <Box></Box>
        )}
      </Flex>
    </>
  )
}
